"use client"

import { useEffect, useState } from "react"

type Theme = "light" | "dark" | "system"

export function useDarkMode() {
  // Cambiamos el valor inicial a 'light' en lugar de 'system'
  const [theme, setTheme] = useState<Theme>("light")
  const [mounted, setMounted] = useState(false)

  // Detecta el tema del sistema - solo se ejecuta en el cliente
  const detectSystemTheme = (): "light" | "dark" => {
    if (typeof window === "undefined") return "light" // Valor por defecto para SSR
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
  }

  // Aplica el tema al documento - solo se ejecuta en el cliente
  const applyTheme = (newTheme: Theme) => {
    if (typeof window === "undefined") return // No hacer nada durante SSR

    const root = window.document.documentElement
    const isDark =
      newTheme === "dark" ||
      (newTheme === "system" && detectSystemTheme() === "dark")

    // Asegurarse de que la clase 'dark' esté ausente por defecto (modo claro)
    root.classList.toggle("dark", isDark)
    localStorage?.setItem("pandora-theme", newTheme)
  }

  // Cambia el tema
  const setMode = (newTheme: Theme) => {
    setTheme(newTheme)
    applyTheme(newTheme)
  }

  // Escucha cambios en las preferencias del sistema - solo se ejecuta en el cliente
  useEffect(() => {
    if (typeof window === "undefined") return // No hacer nada durante SSR

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = () => {
      if (theme === "system") {
        applyTheme("system")
      }
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [theme])

  // Inicializa el tema al montar el componente - solo se ejecuta en el cliente
  useEffect(() => {
    if (typeof window === "undefined") return // No hacer nada durante SSR

    const savedTheme = localStorage?.getItem("pandora-theme") as Theme | null
    if (savedTheme) {
      setTheme(savedTheme)
      applyTheme(savedTheme)
    } else {
      // Si no hay tema guardado, usar 'light' en lugar de 'system'
      setTheme("light")
      applyTheme("light")
    }
    setMounted(true)
  }, [])

  return {
    theme,
    setTheme: setMode,
    isDark:
      mounted &&
      (theme === "dark" ||
        (theme === "system" && detectSystemTheme() === "dark")),
    isLight:
      mounted &&
      (theme === "light" ||
        (theme === "system" && detectSystemTheme() === "light")),
    mounted,
  }
}
